import { createTheme } from '@mui/material/styles';

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: { 
      main: '#163832', // Dark forest green
      dark: '#0E2520', // Darker variant
      light: '#294F49'  // Lighter variant
    },
    secondary: { 
      main: '#607D8B', // Blue-gray (but more gray than blue)
      dark: '#455A64', // Darker blue-gray
      light: '#90A4AE'  // Lighter blue-gray
    },
    background: {
      default: mode === 'dark' ? '#1B1C1D' : '#f8f9fa', // Dark gray with slight blue tint or light gray
      paper: mode === 'dark' ? '#1B1C1D' : '#ffffff'    // Same dark color for paper or white
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#2C3E50',   // White or dark gray
      secondary: mode === 'dark' ? '#B0BEC5' : '#546E7A'  // Light gray or medium gray
    },
    // Additional colors used throughout the app
    info: {
      main: '#4CAF50', // Green for information (replacing blue)
    },
    success: {
      main: '#2E7D32', // Green for success
    },
    warning: {
      main: '#FF9800', // Orange for warnings
    },
    error: {
      main: '#D32F2F', // Red for errors
    },
    divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'
  },
  typography: {
    fontFamily: '"Noto Sans", Arial, sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 800,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '0',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '0',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 700,
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    body1: {
      lineHeight: 1.7,
      fontSize: '1.05rem',
    },
    body2: {
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    subtitle1: {
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    }
  },
  spacing: factor => `${0.5 * factor}rem`,
  shape: {
    borderRadius: 8
  },
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          transition: 'background-color 0.3s ease',
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.primary.light,
            },
          },
          '& .MuiFormLabel-root.Mui-focused': {
            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '0.5rem 1.25rem',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255, 255, 255, 0.1)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover::after': {
            opacity: 1,
          }
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(0)',
          }
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          }
        },
        containedPrimary: {
          backgroundImage: `linear-gradient(to right, #163832, #294F49)`,
        },
        containedSecondary: {
          backgroundImage: `linear-gradient(to right, #455A64, #607D8B)`,
        }
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(10px)',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1)',
          }
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1.5rem',
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '1rem 1.5rem',
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '2rem',
          paddingRight: '2rem',
          '@media (max-width: 600px)': {
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '2rem 0',
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          transition: 'color 0.2s ease',
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'transform 0.2s ease, background-color 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          }
        }
      }
    }
  },
});

export default getTheme; 